import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, MessageCircle, Save, Settings, Clock, ShieldCheck, Trash2, TriangleAlert, Loader2, DatabaseZap, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const BACKEND_URL = "";

const SAMPLE_PRESETS = [
  ["grocery", "Grocery"],
  ["hardware", "Hardware"],
  ["clothing", "Clothing"],
  ["electronics", "Electronics"],
  ["beauty-personal-care", "Beauty & Personal Care"],
  ["home-kitchen", "Home & Kitchen"],
  ["bakery-restaurant", "Bakery or Restaurant"],
  ["pet-supplies", "Pet Supplies"],
  ["cleaning-solutions", "Cleaning Solutions"],
  ["mixed-store", "Mixed Store"],
];

const SettingsPage = () => {
  const [callingNumber, setCallingNumber] = useState("");
  const [whatsappNumber, setWhatsappNumber] = useState("");
  const [orderStartTime, setOrderStartTime] = useState("");
  const [orderEndTime, setOrderEndTime] = useState("");
  const [whatsappPhoneNumberId, setWhatsappPhoneNumberId] = useState("");
  const [whatsappAccessToken, setWhatsappAccessToken] = useState("");
  const [whatsappTemplateName, setWhatsappTemplateName] = useState("");
  const [showCriticalData, setShowCriticalData] = useState(false);
  const [loading, setLoading] = useState(true);
  const [savingContact, setSavingContact] = useState(false);
  const [savingTiming, setSavingTiming] = useState(false);
  const [savingWhatsappApi, setSavingWhatsappApi] = useState(false);
  const [clearDialogOpen, setClearDialogOpen] = useState(false);
  const [clearConfirmation, setClearConfirmation] = useState("");
  const [clearingData, setClearingData] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState("");
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importingData, setImportingData] = useState(false);

  const authHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("ecomdemo_token")}` },
  });

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/settings`, authHeaders());
      setCallingNumber(res.data.calling_number || "");
      setWhatsappNumber(res.data.whatsapp_number || "");
      setOrderStartTime(res.data.order_start_time || "");
      setOrderEndTime(res.data.order_end_time || "");
      setWhatsappPhoneNumberId(res.data.whatsapp_phone_number_id || "");
      setWhatsappAccessToken(res.data.whatsapp_access_token || "");
      setWhatsappTemplateName(res.data.whatsapp_template_name || "");
    } catch {
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSettings(); }, []);

  const handleSaveContact = async () => {
    setSavingContact(true);
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/settings`,
        { calling_number: callingNumber, whatsapp_number: whatsappNumber },
        authHeaders()
      );
      toast.success("Contact numbers saved!");
    } catch {
      toast.error("Failed to save contact numbers");
    } finally {
      setSavingContact(false);
    }
  };

  const handleSaveTiming = async () => {
    setSavingTiming(true);
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/settings`,
        { order_start_time: orderStartTime, order_end_time: orderEndTime },
        authHeaders()
      );
      toast.success("Order timing saved!");
    } catch {
      toast.error("Failed to save order timing");
    } finally {
      setSavingTiming(false);
    }
  };

  const handleSaveWhatsappApi = async () => {
    if (!whatsappPhoneNumberId.trim() || !whatsappAccessToken.trim() || !whatsappTemplateName.trim()) {
      toast.error("Phone Number ID, Access Token, and Template Name are required");
      return;
    }
    setSavingWhatsappApi(true);
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/settings`,
        {
          whatsapp_phone_number_id: whatsappPhoneNumberId.trim(),
          whatsapp_access_token: whatsappAccessToken.trim(),
          whatsapp_template_name: whatsappTemplateName.trim(),
        },
        authHeaders()
      );
      setWhatsappPhoneNumberId("***");
      setWhatsappAccessToken("***");
      toast.success("WhatsApp API details saved!");
    } catch {
      toast.error("Failed to save WhatsApp API details");
    } finally {
      setSavingWhatsappApi(false);
    }
  };

  const handleClearAllData = async (event) => {
    event.preventDefault();
    if (clearConfirmation !== "CLEAR") return;

    setClearingData(true);
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/clear-all-data`, authHeaders());
      toast.success("Masters, orders, users, and delivery managers cleared");
      setClearConfirmation("");
      setClearDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to clear data");
    } finally {
      setClearingData(false);
    }
  };

  const selectedPresetLabel = SAMPLE_PRESETS.find(([key]) => key === selectedPreset)?.[1];

  const handleImportSampleData = async (event) => {
    event.preventDefault();
    if (!selectedPreset) return;

    setImportingData(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/sample-data/${selectedPreset}`,
        {},
        authHeaders()
      );
      const { categories_created: categories, products_created: products } = response.data;
      toast.success(`${selectedPresetLabel} imported: ${categories} categories and ${products} products`);
      setImportDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to import sample data");
    } finally {
      setImportingData(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#6C42F5]" />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-[#F1EDFF] rounded-lg">
            <Settings size={22} className="text-[#6C42F5]" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
            <p className="text-gray-500 text-sm mt-0.5">Manage your app configuration</p>
          </div>
        </div>
      </div>

      {/* Two column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* Contact Numbers */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="p-6 border-b border-gray-100">
            <h2 className="text-base font-semibold text-gray-800 flex items-center gap-2">
              <Phone size={16} className="text-[#6C42F5]" />
              Contact Numbers
            </h2>
            <p className="text-sm text-gray-500 mt-0.5">Shown to customers in the app.</p>
          </div>
          <div className="p-6 space-y-5">
            <div className="space-y-1.5">
              <Label htmlFor="calling_number" className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <Phone size={14} className="text-[#6C42F5]" />
                Calling Number
              </Label>
              <Input
                id="calling_number"
                type="tel"
                placeholder="e.g. +91 98765 43210"
                value={callingNumber}
                onChange={(e) => setCallingNumber(e.target.value)}
                className="h-10"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="whatsapp_number" className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <MessageCircle size={14} className="text-green-600" />
                WhatsApp Number
              </Label>
              <Input
                id="whatsapp_number"
                type="tel"
                placeholder="e.g. +91 98765 43210"
                value={whatsappNumber}
                onChange={(e) => setWhatsappNumber(e.target.value)}
                className="h-10"
              />
            </div>
          </div>
          <div className="px-6 pb-6">
            <Button onClick={handleSaveContact} disabled={savingContact} className="bg-[#6C42F5] hover:bg-[#5631D6] text-white gap-2">
              <Save size={15} />
              {savingContact ? "Saving…" : "Save"}
            </Button>
          </div>
        </div>

        {/* Order Timing */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="p-6 border-b border-gray-100">
            <h2 className="text-base font-semibold text-gray-800 flex items-center gap-2">
              <Clock size={16} className="text-[#8C6CFF]" />
              Order Timing
            </h2>
            <p className="text-sm text-gray-500 mt-0.5">Set daily window during which orders are accepted.</p>
          </div>
          <div className="p-6 space-y-5">
            <div className="space-y-1.5">
              <Label htmlFor="order_start_time" className="text-sm font-medium text-gray-700">
                Start Time
              </Label>
              <Input
                id="order_start_time"
                type="time"
                value={orderStartTime}
                onChange={(e) => setOrderStartTime(e.target.value)}
                className="h-10"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="order_end_time" className="text-sm font-medium text-gray-700">
                End Time
              </Label>
              <Input
                id="order_end_time"
                type="time"
                value={orderEndTime}
                onChange={(e) => setOrderEndTime(e.target.value)}
                className="h-10"
              />
            </div>
            {orderStartTime && orderEndTime && (
              <p className="text-xs text-gray-400 bg-gray-50 rounded-lg px-3 py-2">
                Orders accepted from <span className="font-semibold text-gray-600">{orderStartTime}</span> to <span className="font-semibold text-gray-600">{orderEndTime}</span> daily.
              </p>
            )}
          </div>
          <div className="px-6 pb-6">
            <Button onClick={handleSaveTiming} disabled={savingTiming} className="bg-[#8C6CFF] hover:bg-[#7355E8] text-white gap-2">
              <Save size={15} />
              {savingTiming ? "Saving…" : "Save"}
            </Button>
          </div>
        </div>

        {/* WhatsApp API Credentials */}
        <div className="bg-white rounded-xl border border-red-200 shadow-sm lg:col-span-2">
          <div className="p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h2 className="text-base font-semibold text-gray-800 flex items-center gap-2">
                <ShieldCheck size={17} className="text-red-600" />
                WhatsApp API Configuration
              </h2>
              <p className="text-sm text-gray-500 mt-1">Credentials used to send WhatsApp OTP messages.</p>
            </div>
            <div className="flex items-center gap-3">
              <Label htmlFor="show-critical-data" className="text-sm font-semibold text-gray-700 cursor-pointer">
                Show critical data
              </Label>
              <Switch
                id="show-critical-data"
                checked={showCriticalData}
                onCheckedChange={setShowCriticalData}
                className="data-[state=checked]:bg-red-600"
              />
            </div>
          </div>

          {showCriticalData && (
            <div className="border-t border-red-100 p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="space-y-1.5">
                  <Label htmlFor="whatsapp_phone_number_id" className="text-sm font-medium text-gray-700">
                    Phone Number ID
                  </Label>
                  <Input
                    id="whatsapp_phone_number_id"
                    value={whatsappPhoneNumberId}
                    onChange={(event) => setWhatsappPhoneNumberId(event.target.value)}
                    placeholder="Enter your Meta phone number ID"
                    autoComplete="off"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="whatsapp_template_name" className="text-sm font-medium text-gray-700">
                    Template Name
                  </Label>
                  <Input
                    id="whatsapp_template_name"
                    value={whatsappTemplateName}
                    onChange={(event) => setWhatsappTemplateName(event.target.value)}
                    placeholder="e.g. ecomdemo_otp_template"
                    autoComplete="off"
                  />
                </div>
                <div className="space-y-1.5 md:col-span-2">
                  <Label htmlFor="whatsapp_access_token" className="text-sm font-medium text-gray-700">
                    Access Token
                  </Label>
                  <Input
                    id="whatsapp_access_token"
                    value={whatsappAccessToken}
                    onChange={(event) => setWhatsappAccessToken(event.target.value)}
                    placeholder="Enter the Meta WhatsApp access token"
                    autoComplete="new-password"
                    className="font-mono text-xs"
                  />
                </div>
              </div>
              <div className="mt-6 flex items-center justify-between gap-4">
                <p className="text-xs text-red-600">Only reveal this section when you need to view or update these credentials.</p>
                <Button
                  onClick={handleSaveWhatsappApi}
                  disabled={savingWhatsappApi}
                  className="bg-red-600 hover:bg-red-700 text-white gap-2 shrink-0"
                >
                  <Save size={15} />
                  {savingWhatsappApi ? "Saving…" : "Save Critical Data"}
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Demo Data Import */}
        <div className="bg-white rounded-xl border border-[#6C42F5]/20 shadow-sm lg:col-span-2 overflow-hidden">
          <div className="p-6 border-b border-gray-100">
            <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
              <DatabaseZap size={18} className="text-[#6C42F5]" />
              Demo Data Import
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              Replace the current catalogue with a polished industry-specific demo.
            </p>
          </div>
          <div className="p-6 flex flex-col md:flex-row md:items-end gap-4">
            <div className="space-y-2 flex-1">
              <Label htmlFor="sample-preset" className="text-sm font-medium text-gray-700">Store type</Label>
              <Select value={selectedPreset} onValueChange={setSelectedPreset} disabled={importingData}>
                <SelectTrigger id="sample-preset" className="w-full h-11">
                  <SelectValue placeholder="Select a sample catalogue" />
                </SelectTrigger>
                <SelectContent>
                  {SAMPLE_PRESETS.map(([key, label]) => <SelectItem key={key} value={key}>{label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <AlertDialog open={importDialogOpen} onOpenChange={(open) => !importingData && setImportDialogOpen(open)}>
              <AlertDialogTrigger asChild>
                <Button disabled={!selectedPreset || importingData} className="h-11 bg-[#6C42F5] hover:bg-[#5631D6] text-white gap-2 md:min-w-36">
                  <Download size={16} /> Import Data
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <div className="w-11 h-11 rounded-full bg-[#F1EDFF] text-[#6C42F5] flex items-center justify-center mb-2">
                    <DatabaseZap size={22} />
                  </div>
                  <AlertDialogTitle className="font-outfit text-xl">Import {selectedPresetLabel} sample data?</AlertDialogTitle>
                  <AlertDialogDescription className="leading-6">
                    This will replace all current categories, products, banners, payment methods, and the store highlight. Orders, users, stores, delivery managers, admin access, and global settings will not be changed.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel disabled={importingData}>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleImportSampleData} disabled={importingData} className="bg-[#6C42F5] text-white hover:bg-[#5631D6]">
                    {importingData ? <><Loader2 size={16} className="mr-2 animate-spin" /> Importing…</> : "Replace & Import"}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
          <div className="px-6 pb-6">
            <p className="text-xs text-gray-400">Each preset includes 5 categories, 25 products, one banner, three payment methods, and a store highlight.</p>
          </div>
        </div>

        {/* Danger Zone */}
        <div className="bg-white rounded-xl border border-red-200 shadow-sm lg:col-span-2 overflow-hidden">
          <div className="p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-red-50 rounded-lg shrink-0">
                <TriangleAlert size={20} className="text-red-600" />
              </div>
              <div>
                <h2 className="text-base font-semibold text-gray-900">Clear all business data</h2>
                <p className="text-sm text-gray-500 mt-1 max-w-2xl">
                  Permanently removes masters, orders, customers, store admins, and delivery managers. Your super-admin login and app settings are preserved.
                </p>
              </div>
            </div>

            <AlertDialog open={clearDialogOpen} onOpenChange={(open) => {
              if (!clearingData) {
                setClearDialogOpen(open);
                if (!open) setClearConfirmation("");
              }
            }}>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" className="gap-2 shrink-0 bg-red-600 hover:bg-red-700">
                  <Trash2 size={16} /> Clear All Data
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <div className="w-11 h-11 rounded-full bg-red-100 text-red-600 flex items-center justify-center mb-2">
                    <TriangleAlert size={22} />
                  </div>
                  <AlertDialogTitle className="font-outfit text-xl">Permanently clear all data?</AlertDialogTitle>
                  <AlertDialogDescription className="leading-6">
                    This cannot be undone. Categories, products, banners, payment methods, highlights, orders, users, addresses, carts, OTPs, store admins, and delivery managers will be deleted.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <div className="space-y-2 py-2">
                  <Label htmlFor="clear-confirmation" className="text-sm font-medium text-gray-700">
                    Type <span className="font-bold text-red-600">CLEAR</span> to confirm
                  </Label>
                  <Input
                    id="clear-confirmation"
                    value={clearConfirmation}
                    onChange={(event) => setClearConfirmation(event.target.value)}
                    placeholder="CLEAR"
                    autoComplete="off"
                    disabled={clearingData}
                  />
                </div>
                <AlertDialogFooter>
                  <AlertDialogCancel disabled={clearingData}>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleClearAllData}
                    disabled={clearConfirmation !== "CLEAR" || clearingData}
                    className="bg-red-600 text-white hover:bg-red-700 focus:ring-red-600"
                  >
                    {clearingData ? <><Loader2 size={16} className="mr-2 animate-spin" /> Clearing…</> : "Clear Everything"}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>

      </div>
    </div>
  );
};

export default SettingsPage;
